import React, { useState, useEffect } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from "firebase/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth } from "../firebase";
import { httpsCallable, getFunctions } from "firebase/functions";
import { Icons } from "../lib/Icons";

const SoloSignupPage = ({ isBeta = false }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [isLogin, setIsLogin] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resetDisabledUntil, setResetDisabledUntil] = useState(0);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isSchoolUser, setIsSchoolUser] = useState(false);
  const [schoolRedirectCountdown, setSchoolRedirectCountdown] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Robust check for beta status: prop OR URL path
  const isBetaRoute = isBeta || window.location.pathname.includes('/beta');

  // Get redirect params from URL (for SEO landing pages)
  const resourceSlug = searchParams.get('resource');
  const articleSlug = searchParams.get('article');
  const returnTo = searchParams.get('returnTo');
  
  // Determine where to redirect after signup
  const getRedirectUrl = () => {
    if (returnTo) return returnTo;
    if (resourceSlug) return `/solo/activity/${resourceSlug}`;
    if (articleSlug) return `/solo/articles/${articleSlug}`;
    return '/solo/dashboard';
  };

  // Set initial mode based on URL params
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'login') {
      setIsLogin(true);
    }
  }, [searchParams]);

  // Check auth state and detect school users
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        // Check if this is a solo user with an existing subscription
        const functions = getFunctions(undefined, 'europe-west3');
        const checkSubscription = httpsCallable(functions, 'checkSoloSubscriptionFunc');
        try {
          const result = await checkSubscription();
          
          // Detect school users and show redirect message
          if (result.data.userType === 'school' || result.data.status === 'school_user') {
            setIsSchoolUser(true);
            setSchoolRedirectCountdown(5);
            return;
          }
          
          if (result.data.hasActiveSubscription) {
            // Already subscribed, go to requested resource or dashboard
            navigate(getRedirectUrl());
          }
          // Otherwise they need to subscribe - stay on this page or redirect to checkout
        } catch (e) {
          console.log('Subscription check failed, likely new user');
        }
      } else {
        setUser(null);
        setIsSchoolUser(false);
        setSchoolRedirectCountdown(null);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Auto-redirect countdown for school users
  useEffect(() => {
    if (schoolRedirectCountdown === null) return;
    
    if (schoolRedirectCountdown <= 0) {
      navigate('/dashboard');
      return;
    }
    
    const timer = setTimeout(() => {
      setSchoolRedirectCountdown(prev => prev - 1);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [schoolRedirectCountdown, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");

    // Validation
    if (!isLogin && password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!isLogin && password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      let userCredential;
      if (isLogin) {
        userCredential = await signInWithEmailAndPassword(auth, email, password);
        console.log("✅ Solo user logged in successfully");
        
        // Check subscription and redirect
        const functions = getFunctions(undefined, 'europe-west3');
        const checkSubscription = httpsCallable(functions, 'checkSoloSubscriptionFunc');
        const result = await checkSubscription();
        
        // Handle case where user exists in Auth but has no Firestore profile
        // This can happen after password reset if profile was never created
        if (result.data.status === 'none' && !result.data.userType) {
          console.log("📝 Creating missing user profile...");
          try {
            const createSoloUser = httpsCallable(functions, 'createSoloUserFunc');
            await createSoloUser({
              email: email,
              displayName: email.split('@')[0],
            });
            console.log("✅ User profile created");
          } catch (profileError) {
            // Profile might already exist (race condition), continue anyway
            console.log("Profile creation skipped:", profileError.message);
          }
        }
        
        // Beta testers or users with active subscriptions go to requested resource or dashboard
        if (result.data.hasActiveSubscription || result.data.isBetaTester) {
          navigate(getRedirectUrl());
        } else {
          // Need to subscribe - redirect to checkout with return URL
          const redirectUrl = getRedirectUrl();
          navigate(`/solo/checkout${redirectUrl !== '/solo/dashboard' ? `?returnTo=${encodeURIComponent(redirectUrl)}` : ''}`);
        }
      } else {
        // Create account
        userCredential = await createUserWithEmailAndPassword(auth, email, password);
        console.log("✅ Solo user account created");

        // Create solo user profile with marketing consent
        const functions = getFunctions(undefined, 'europe-west3');
        const createSoloUser = httpsCallable(functions, 'createSoloUserFunc');
        
        console.log(`[BETA CHECK] Creating user via frontend. Prop isBeta: ${isBeta}, Path: ${window.location.pathname}, Final isBetaRoute: ${isBetaRoute}`);

        await createSoloUser({
          email: email,
          displayName: displayName || email.split('@')[0],
          marketingConsent: marketingConsent,
          isBetaTester: isBetaRoute,
        });

        console.log("✅ Solo user profile created");

        // Beta testers skip checkout and go straight to requested resource or dashboard
        // Regular users also go to dashboard (freemium model - paywall is on activity detail)
        navigate(getRedirectUrl());
      }
    } catch (error) {
      console.error("❌ Auth error:", error.message);
      
      // User-friendly error messages
      if (error.code === 'auth/email-already-in-use') {
        setError("An account with this email already exists. Try signing in instead.");
      } else if (error.code === 'auth/invalid-email') {
        setError("Please enter a valid email address.");
      } else if (error.code === 'auth/weak-password') {
        setError("Please choose a stronger password (at least 6 characters).");
      } else if (error.code === 'auth/user-not-found' || 
                 error.code === 'auth/wrong-password' || 
                 error.code === 'auth/invalid-credential') {
        setError("Invalid email or password. Please check your details and try again.");
      } else if (error.code === 'auth/too-many-requests') {
        setError("Too many failed attempts. Please try again later or reset your password.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError('Enter your email above, then click "Forgot password?"');
      return;
    }
    try {
      // Use custom branded email via SendGrid
      const functions = getFunctions(undefined, 'europe-west3');
      const generateResetLink = httpsCallable(functions, 'generatePasswordResetLinkFunc');
      await generateResetLink({ email });
      
      setResetDisabledUntil(Date.now() + 30_000);
      setError("");
      alert("Password reset email sent! Check your inbox.");
    } catch (e) {
      console.error('Reset error:', e);
      // Don't reveal if email exists or not for security
      setResetDisabledUntil(Date.now() + 30_000);
      setError("");
      alert("If an account exists with this email, you'll receive a password reset link.");
    }
  };

  const canSendReset = Date.now() >= resetDisabledUntil;
  const resetSecondsLeft = Math.max(0, Math.ceil((resetDisabledUntil - Date.now()) / 1000));

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOGM5Ljk0MSAwIDE4LTguMDU5IDE4LTE4cy04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjAzIi8+PC9nPjwvc3ZnPg==')] opacity-30"></div>
      
      <div className="relative flex min-h-screen flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center gap-3">
              <img 
                src="/brand/logo-thumbnail-purple.png"
                alt="SEN-Ed" 
                className="w-12 h-12 rounded-xl"
              />
              <div>
                <h1 className="text-2xl font-bold text-white">SEN-Ed Resources</h1>
                <p className="text-sm text-purple-200">Individual Teacher Access</p>
              </div>
            </div>
          </div>
          
          <button
            onClick={() => navigate('/')}
            className="block mx-auto text-sm text-purple-200 hover:text-white transition-colors"
          >
            ← Back to home
          </button>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          {/* School user redirect notification */}
          {isSchoolUser && (
            <div className="bg-blue-500/20 backdrop-blur-lg px-6 py-8 shadow-2xl rounded-2xl border border-blue-400/30 mb-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icons.School className="w-8 h-8 text-blue-300" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">
                  You're Already Registered With a School!
                </h2>
                <p className="text-blue-100 text-sm mb-6">
                  Your school account gives you access to even more features including student tracking, 
                  intervention management, and progress reports.
                </p>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-3 text-white font-semibold shadow-lg hover:from-blue-600 hover:to-indigo-700 transition-all mb-3"
                >
                  Go to School Portal
                </button>
                <p className="text-blue-200/60 text-xs">
                  Redirecting in {schoolRedirectCountdown} seconds...
                </p>
              </div>
            </div>
          )}

          {!isSchoolUser && (
          <>
          <div className="bg-white/10 backdrop-blur-lg px-6 py-10 shadow-2xl rounded-2xl border border-white/20">
            <div className="mb-8 text-center">
              {isBetaRoute && !isLogin && (
                <div className="mb-4 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-full border border-green-400/30">
                  <Icons.Sparkles className="w-4 h-4 text-green-300" />
                  <span className="text-green-100 font-semibold text-sm">Beta Tester Access</span>
                </div>
              )}
              <h2 className="text-2xl font-bold text-white mb-2">
                {isLogin ? "Welcome Back" : "Create Your Account"}
              </h2>
              <p className="text-purple-200 text-sm">
                {isLogin 
                  ? "Sign in to access your SEN resources" 
                  : isBetaRoute
                    ? "Beta tester access - full features, no payment required"
                    : "Get access to hundreds of SEN teaching resources"}
              </p>
            </div>

            {/* Registration info banner - contextualized if coming from public resource */}
            {!isLogin && (
              <div className="mb-6 p-4 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl border border-purple-400/30">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                    <Icons.BookOpen className="w-5 h-5 text-purple-300" />
                  </div>
                  <div>
                    {(resourceSlug || articleSlug) ? (
                      <>
                        <p className="text-purple-100 font-semibold">Create your free account to continue</p>
                        <p className="text-purple-200/80 text-sm">Get full access to this resource and hundreds more.</p>
                      </>
                    ) : (
                      <>
                        <p className="text-purple-100 font-semibold">Access hundreds of SEN resources</p>
                        <p className="text-purple-200/80 text-sm">Activities, videos, and strategies for every need.</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-red-500/20 rounded-xl border border-red-400/30">
                <p className="text-red-100 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div>
                  <label htmlFor="displayName" className="block text-sm font-medium text-purple-100 mb-2">
                    Your Name
                  </label>
                  <input
                    id="displayName"
                    name="displayName"
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Enter your name"
                    className="block w-full rounded-xl border-0 bg-white/10 px-4 py-3 text-white placeholder-purple-300/50 backdrop-blur-sm focus:ring-2 focus:ring-purple-400 focus:outline-none transition-all"
                  />
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-purple-100 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="block w-full rounded-xl border-0 bg-white/10 px-4 py-3 text-white placeholder-purple-300/50 backdrop-blur-sm focus:ring-2 focus:ring-purple-400 focus:outline-none transition-all"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-purple-100 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    autoComplete={isLogin ? "current-password" : "new-password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="block w-full rounded-xl border-0 bg-white/10 px-4 py-3 pr-11 text-white placeholder-purple-300/50 backdrop-blur-sm focus:ring-2 focus:ring-purple-400 focus:outline-none transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-purple-300/70 hover:text-white transition-colors rounded"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <Icons.EyeSlash className="w-5 h-5" /> : <Icons.Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {!isLogin && (
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-purple-100 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="block w-full rounded-xl border-0 bg-white/10 px-4 py-3 pr-11 text-white placeholder-purple-300/50 backdrop-blur-sm focus:ring-2 focus:ring-purple-400 focus:outline-none transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((p) => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-purple-300/70 hover:text-white transition-colors rounded"
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? <Icons.EyeSlash className="w-5 h-5" /> : <Icons.Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              )}

              {isLogin && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={canSendReset ? handlePasswordReset : undefined}
                    disabled={!canSendReset}
                    className="text-sm text-purple-200 hover:text-white transition-colors disabled:opacity-50"
                  >
                    {canSendReset ? 'Forgot password?' : `Reset link sent (${resetSecondsLeft}s)`}
                  </button>
                </div>
              )}

              {/* Marketing consent checkbox - only on signup */}
              {!isLogin && (
                <div className="flex items-start gap-3">
                  <input
                    id="marketingConsent"
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-purple-300 bg-white/10 text-purple-500 focus:ring-purple-400 focus:ring-offset-0"
                  />
                  <label htmlFor="marketingConsent" className="text-sm text-purple-200/80 cursor-pointer">
                    Send me teaching tips, new resources, and updates (you can unsubscribe anytime)
                  </label>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 px-4 py-3 text-white font-semibold shadow-lg hover:from-purple-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 focus:ring-offset-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing...
                  </span>
                ) : isLogin ? (
                  "Sign In"
                ) : (
                  "Register Now"
                )}
              </button>
            </form>

            {/* Features list for signup */}
            {!isLogin && (
              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="text-sm font-medium text-purple-100 mb-4">What you'll get:</p>
                <ul className="space-y-3">
                  {[
                    "Access to 100+ SEN teaching activities",
                    "Video resources for every strategy",
                    "Primary & Secondary sections",
                    "AI-powered resource search",
                    "New resources added regularly",
                  ].map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-sm text-purple-200">
                      <span className="flex-shrink-0 w-5 h-5 bg-green-500/20 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <p className="mt-6 text-center text-sm text-purple-200">
            {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError("");
              }}
              className="font-semibold text-white hover:text-purple-200 transition-colors"
            >
              {isLogin ? "Register now" : "Sign in"}
            </button>
          </p>

          {/* School signup link */}
          <p className="mt-4 text-center text-sm text-purple-300/70">
            Are you a school?{" "}
            <button
              onClick={() => navigate('/login?mode=auth')}
              className="text-purple-200 hover:text-white transition-colors"
            >
              Register your school here →
            </button>
          </p>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SoloSignupPage;


